import { useRef, useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    IconButton,
    Fab,
} from '@mui/material';
import {
    MdCheckCircle,
    MdDraw,
    MdLocationOn,
    MdDescription,
    MdPersonAdd,
    MdBugReport,
    MdClose,
    MdArrowForward,
    MdKeyboardDoubleArrowDown,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import logoLocaSmartCheck from '../../assets/logo-locasmartcheck.png';

interface ModalApresentacaoProps {
    open: boolean;
    onClose: () => void;
    onInteresse: () => void;
}

const features = [
    { icon: MdCheckCircle, text: 'Modelos de Vistorias 100% personalizáveis', desc: 'Crie seus próprios checklists de vistoria' },
    { icon: MdDraw, text: 'Assinatura do Motorista e Vistoriador', desc: 'Coleta digital de assinaturas no app' },
    { icon: MdLocationOn, text: 'Localização em tempo real', desc: 'GPS registrado automaticamente na vistoria' },
    { icon: MdDescription, text: 'Relatório/Laudo de Vistoria', desc: 'PDF profissional gerado automaticamente' },
    { icon: MdPersonAdd, text: 'Acesso do Condutor + Link Externo', desc: 'Envie vistorias para motoristas via link' },
    { icon: MdBugReport, text: 'Controle de Avarias', desc: 'Registro detalhado com fotos e observações' },
];

function ModalApresentacao({ open, onClose, onInteresse }: ModalApresentacaoProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const precoRef = useRef<HTMLDivElement>(null);
    const [mostrarSeta, setMostrarSeta] = useState(true);

    const verificarScroll = useCallback(() => {
        const el = contentRef.current;
        if (!el) return;
        const chegouNoFinal = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
        setMostrarSeta(!chegouNoFinal);
    }, []);

    useEffect(() => {
        if (open) {
            setMostrarSeta(true);
            // Verificar após o dialog renderizar
            setTimeout(verificarScroll, 300);
        }
    }, [open, verificarScroll]);

    const handleScrollDown = () => {
        const el = contentRef.current;
        if (!el) return;
        el.scrollBy({ top: 300, behavior: 'smooth' });
    };

    const handleScrollToPreco = () => {
        precoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{
                sx: { borderRadius: 3, overflow: 'hidden', position: 'relative' }
            }}
        >
            <Box sx={{
                background: 'linear-gradient(135deg, #0a1929 0%, #132f4c 50%, #1a3a5c 100%)',
                color: 'white',
                p: 2.5,
                position: 'relative',
            }}>
                <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
                    <MdClose size={20} />
                </IconButton>
                <Box sx={{ textAlign: 'center' }}>
                    <Box
                        component="img"
                        src={logoLocaSmartCheck}
                        alt="LocaSmartCheck"
                        sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', mb: 0.5 }}
                    />
                    <Typography variant="h5" fontWeight="800" sx={{ color: '#fff' }}>
                        LocaSmartCheck
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                        Seu app de vistorias veiculares profissionais
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleScrollToPreco}
                        endIcon={<MdKeyboardDoubleArrowDown />}
                        sx={{
                            mt: 1.5,
                            px: 3,
                            py: 0.8,
                            fontSize: 14,
                            fontWeight: 700,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                            color: '#fff',
                            boxShadow: '0 3px 12px rgba(37, 211, 102, 0.4)',
                            '&:hover': {
                                boxShadow: '0 5px 18px rgba(37, 211, 102, 0.6)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s',
                        }}
                    >
                        Assinar Agora
                    </Button>
                </Box>
            </Box>

            <DialogContent
                ref={contentRef}
                onScroll={verificarScroll}
                sx={{ p: 0, position: 'relative' }}
            >
                {/* Video */}
                <Box sx={{ px: 3, pt: 3 }}>
                    <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000', borderRadius: 2, overflow: 'hidden' }}>
                        <iframe
                            src="https://www.youtube.com/embed/0UtHbl-iexs"
                            title="LocaSmartCheck"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                </Box>

                {/* Features Grid */}
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="700" sx={{ textAlign: 'center', mb: 3, color: '#132f4c' }}>
                        Funcionalidades incluídas
                    </Typography>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                    }}>
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <Box key={index} sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: '#f0f4f8',
                                    border: '1px solid #d6e4f0',
                                    transition: 'all 0.2s',
                                    '&:hover': { backgroundColor: '#d6e4f0', transform: 'translateY(-1px)' },
                                }}>
                                    <Box sx={{
                                        backgroundColor: '#132f4c',
                                        borderRadius: '50%',
                                        minWidth: 36,
                                        height: 36,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Icon style={{ color: 'white', fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" fontWeight="600" sx={{ color: '#132f4c' }}>
                                            {feature.text}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {feature.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Preco destaque */}
                    <Box ref={precoRef} sx={{
                        mt: 3,
                        p: 2.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #e3edf7 0%, #cdd9e5 100%)',
                        border: '2px solid #5a8bb5',
                        textAlign: 'center',
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            A partir de
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="h6" color="text.secondary">R$</Typography>
                            <Typography variant="h3" fontWeight="800" sx={{ color: '#132f4c' }}>19,90</Typography>
                            <Typography variant="body1" color="text.secondary">/mês</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            até 5 placas inclusas | + R$ 3,00 por placa adicional
                        </Typography>
                    </Box>

                    {/* CTA */}
                    <Box sx={{ textAlign: 'center', mt: 3, mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={onInteresse}
                                endIcon={<MdArrowForward />}
                                sx={{
                                    px: 5,
                                    py: 1.5,
                                    fontSize: 16,
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, #0a1929 0%, #1a3a5c 100%)',
                                    boxShadow: '0 4px 15px rgba(10, 25, 41, 0.4)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(10, 25, 41, 0.6)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                            >
                                Quero ativar agora
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                href="https://wa.me/5585991255041?text=Ol%C3%A1%2C%20tenho%20interesse%20no%20LocaSmartCheck!"
                                target="_blank"
                                startIcon={<FaWhatsapp />}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    fontSize: 16,
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, #128C7E 0%, #25D366 100%)',
                                    boxShadow: '0 4px 15px rgba(37, 211, 102, 0.4)',
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(37, 211, 102, 0.6)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                            >
                                Falar no WhatsApp
                            </Button>
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                            <Button variant="text" size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                                Agora não, obrigado
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {/* Botão flutuante para scroll */}
            {mostrarSeta && (
                <Fab
                    size="small"
                    onClick={handleScrollDown}
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        zIndex: 10,
                        background: 'linear-gradient(135deg, #0a1929 0%, #1a3a5c 100%)',
                        color: '#fff',
                        boxShadow: '0 4px 15px rgba(10, 25, 41, 0.5)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #132f4c 0%, #1a3a5c 100%)',
                        },
                        '@keyframes bounceDown': {
                            '0%, 100%': { transform: 'translateY(0)' },
                            '50%': { transform: 'translateY(4px)' },
                        },
                        animation: 'bounceDown 1.5s ease-in-out infinite',
                    }}
                >
                    <MdKeyboardDoubleArrowDown size={24} />
                </Fab>
            )}
        </Dialog>
    );
}

export default ModalApresentacao;
